// src/utils/changelogData.js

/**
 * Changelog data for the Pokémon TCG Deck Builder.
 * Each object in the array represents a version.
 * - version: The version number (string).
 * - date: Release date (string, optional).
 * - title: Descriptive title for the version (string, optional).
 * - isOpen: If true, this version's details are typically shown expanded by default in the UI (boolean).
 * - changes: Array of change categories. Each category has:
 * - title: Title of the category (string).
 * - items: Array of strings describing individual changes.
 * - Keywords (e.g., ADDED:, FIXED:) should be plain text followed by a colon.
 * - Subsection titles within items should be bolded with a colon: **Subsection Title:**
 */
const changelogData = [
    {
        version: "4.1.3",
        title: "Cloudflare SPA routes",
        date: new Date().toISOString().slice(0,10),
        isOpen: true,
        changes: [
            {
                title: "Bug Fixes",
                items: [
                    "FIXED: Cloudflare was sending `/catalog` and `/grid` to the homepage (`308` to `/`). Rewrites now keep the path, and `/catalog/` still opens the catalog.",
                    "FIXED: Header and toast open `/catalog/` (the URL that loads). Bare `/catalog` was a cached 308 to `/`, so buttons and shared links failed.",
                    "FIXED: Homepage search no longer `console.error`s for sets that have no Energy (or other) JSON. `set-index.json` already lists which types exist."
                ]
            },
            {
                title: "Technical Improvements",
                items: [
                    "CHANGED: Canonical catalog URL is `/catalog/`. `functions/_middleware.js` 302s `/catalog` to `/catalog/` (not 308) and serves the app HTML there.",
                    "ADDED: `wrangler.jsonc` with Pages output `build`. Pages does not support the Workers `assets` key.",
                    "ADDED: Support script `scripts/ensure-spa-routes.js` copies `index.html` to `catalog.html` and `grid.html` after build.",
                    "ADDED: `isGridPath` in `src/utils/promoChrome.js`. App routing uses trailing-slash-safe catalog and grid paths."
                ]
            }
        ]
    },
    {
        version: "4.1.2",
        title: "Header, catalog toasts, live metadata",
        date: new Date().toISOString().slice(0,10),
        isOpen: false,
        changes: [
            {
                title: "Major Features",
                items: [
                    "CHANGED: **Try New Builder Now!** sits next to the site name. Play Meta-PTCG, Grid, and the version badge stay on the right.",
                    "ADDED: Optional toast that opens `/catalog`. Closing it stores the choice.",
                    "CHANGED: Discord invite is a compact **Join Discord** control in the site colors, with an optional close.",
                    "ADDED: On `/catalog`, the header badge is red **v1.0** and opens the catalog changelog (`1.0` / `0.9` / `0.8`). The homepage still shows the 4.x badge.",
                    "ADDED: Catalog **Official** tab with a TCG / Pocket slider. Cards come from TCGdex. Homepage search is unchanged.",
                    "CHANGED: Open Graph and Twitter tags use **https://deck.meta-ptcg.org/** and **banner.png**.",
                    "ADDED: CDN Remote and Import accept a pasted GitHub or JSON URL.",
                    "ADDED: Catalog toasts: blue for success and loading, red for errors, on remote load, import, export, CSV download, and set fetch.",
                    "CHANGED: Empty catalog deck hint is a floating note in the drop area.",
                    "CHANGED: Catalog chrome is not text-selectable. Filter and other fields remain selectable.",
                    "CHANGED: Leave-page prompt appears only when unsaved catalog or grid work would be lost. Don't show again is optional."
                ]
            },
            {
                title: "Bug Fixes",
                items: [
                    "FIXED: Discord invite uses a Font Awesome icon instead of missing PNG assets.",
                    "FIXED: Header Play, Grid, and Try New wrap on small screens instead of overlapping the site title.",
                    "FIXED: Homepage no longer shows a constant browser leave/reload warning. Batch Card Creator defaults were treated as unsaved. The native dialog is skipped on `/`. The in-app prompt has a **Don't show this again** checkbox.",
                    "FIXED: Catalog Official cards use TCGdex Pokémon / Trainer / Energy. Paldean Wonders Pocket images load from Limitless. A deck-row Pocket variation slider appears when a name has 2+ prints.",
                ]
            },
            {
                title: "Technical Improvements",
                items: [
                    "ADDED: Support module `src/utils/promoChrome.js` for banner, toast, and Discord visibility.",
                    "ADDED: Support module `src/utils/catalog/remoteJsonUrl.js` for GitHub and gist URLs.",
                    "ADDED: Support module `src/utils/pageLeaveGuard.js` for in-memory leave warnings.",
                    "ADDED: Support module `src/utils/siteMeta.js`; `scripts/copy-site-assets.js` copies `src/assets/banner.png` to `public/banner.png`.",
                    "DEBUG: `/catalog?debug=1` lists toast, Discord, remote URL, leave-prompt, OG image, official types, and Pocket image host."
                ]
            }
        ]
    },
    {
        version: "4.1.1",
        title: "Catalog overlays, Try new, 4-of bypass, Pokecardmaker",
        date: "2026-08-25",
        isOpen: false,
        changes: [
            {
                title: "Major Features",
                items: [
                    "CHANGED: Header catalog entry is now a red glowing **Try new** button, placed left of Play Meta-PTCG.",
                    "CHANGED: Catalog opens on **Forte Arrivals** (first pinned set) instead of Custom.",
                    "CHANGED: Catalog deck builder opens at **half screen** (split) instead of collapsed.",
                    "ADDED: Catalog deck **Bypass 4-of** switch so copies can exceed the usual 4-of rule (60-card deck cap still applies).",
                    "ADDED: Custom **Pokecardmaker** tab: paste a set URL, drag cards into the deck, export the full set in tcg-deck-builder-export JSON with original image URLs (beta; no preloaded index).",
                    "ADDED: Pokecardmaker URL field keeps **recent set URLs** (Down Arrow / chevron) and a local IndexedDB JSON copy so repeat loads skip the network.",
                    "CHANGED: Hide WIP CDN sets SetName, TestMeta, and meta-ptcg from catalog tabs and CDN Remote (`src/data/catalogHiddenSets.json`)."
                ]
            },
            {
                title: "Bug Fixes",
                items: [
                    "FIXED: Catalog set tabs sat under the main navbar; content offset follows the measured header height.",
                    "FIXED: Pokecardmaker gallery images load in the user's browser from cdn.customcardmaker.net (CloudFront / pkmn-proxy mirrors 404); set JSON uses a simple CORS GET without a preflight.",
                    "FIXED: Pokecardmaker help text is a floating toast (readable on dark theme); PCM tabs/URL/buttons stay a fitted header so only the gallery scrolls.",
                    "FIXED: Dash slugs such as the-age-of-megas-z940oxq returned 404 after the CDN underscore rewrite; sets load from api.pokecardmaker.net with original slugs.",
                    "FIXED: Catalog deck CSV is TCG Simulator `QTY,Name,Type,URL` (extra Id/RuleBox columns broke imports).",
                ]
            },
            {
                title: "Technical Improvements",
                items: [
                    "ADDED: `--app-header-height` measured from the fixed navbar; catalog overlay z-index and overflow rules in CatalogPage.css.",
                    "ADDED: Support module `src/utils/catalog/catalogSetVisibility.js` to filter hidden WIP CDN sets.",
                    "ADDED: Support module `src/utils/catalog/catalogDeckCsv.js` for TCG Simulator CSV import/export.",
                    "ADDED: Support module `src/utils/catalog/pcmLocalCache.js` (recent PCM URLs + IndexedDB set JSON; `/catalog?debug=1` shows recents/stored counts).",
                    "DEBUG: Catalog notes updated for Try new, overlay stacking, 4-of bypass, Pokecardmaker set-URL import, hidden WIP sets, and remote JSON URLs.",
                    "DEBUG: Universal catalog debug strip at /catalog?debug=1 (hidden set names, tab counts, PCM URL)."
                ]
            }
        ]
    },
    {
        version: "4.1.0",
        title: "Catalog Deck Builder",
        date: "2026-08-25",
        isOpen: false,
        changes: [
            {
                title: "Major Features",
                items: [
                    "ADDED: `/catalog` page from the header. Homepage Search and Deck layout is unchanged.",
                    "ADDED: Catalog gallery with a collapsible deck drawer that expands to a half-and-half grid.",
                    "ADDED: Custom view with Import, Export, and CDN Remote tabs on the page.",
                    "ADDED: CDN set tabs: pinned list first, then remaining index sets. First 10 are visible; overflow is in More.",
                    "ADDED: WebP-first image loading through cdn.meta-ptcg.org (API proxy when REACT_APP_CDN_USE_API is on). Catalog sets stay in memory, not localStorage."
                ]
            },
            {
                title: "Bug Fixes",
                items: [
                    "FIXED: Same-name cards with different Rule Box types (ex, Mega, V, ACE SPEC) are distinct in the catalog deck via set + number + rule-box identity.",
                    "FIXED: Malie/remote import now keeps RULE_BOX text and appends display-name suffixes so Charizard and Charizard ex no longer collapse together."
                ]
            },
            {
                title: "Technical Improvements",
                items: [
                    "ADDED: Support modules in src/utils/catalog/ (cdnCatalogService, cardIdentity, webpCdn) and pin override src/data/catalogPinnedSets.json.",
                    "ADDED: SPA rewrite for /catalog in public/_redirects.",
                    "DEBUG: Catalog notes in src/components/catalog/NOTES.md (pin rules, WebP/CDN, id-based identity)."
                ]
            }
        ]
    },
    {
        version: "4.0.0",
        title: "Source of Truth - CDN Integration & Remote Set Management",
        date: new Date().toISOString().slice(0,10),
        isOpen: false,
        changes: [
            {
                title: "Major Features",
                items: [
                    "ADDED: Remote URL Set Management with 2 URL limit and auto-refresh support",
                    "ADDED: CDN Search Integration - Real-time search of available sets from cdn.meta-ptcg.org",
                    "ADDED: Enhanced Import/Export Modal - Unified interface with Import, Export, and Remote URL tabs",
                    "ADDED: Add Set Dropdown - Quick access to 'Add Empty Set' and 'Sets & Import' options",
                    "ADDED: Client-side caching (5 minutes) + Cloudflare edge caching optimization",
                    "ADDED: CDN Index Search - Search by category name, set name, or abbreviation"
                ]
            },
            {
                title: "Bug Fixes",
                items: [
                    "FIXED: Critical .toLowerCase() error when viewing All Cards with invalid card data",
                    "FIXED: Add Set button now provides dropdown menu with import/export access",
                    "FIXED: Color scheme improvements for CDN search results (better contrast)",
                    "FIXED: Type safety for card.supertype fields to prevent crashes"
                ]
            },
            {
                title: "Technical Improvements",
                items: [
                    "IMPROVED: Cloudflare edge caching optimization (changed cache mode from 'no-cache' to 'default')",
                    "IMPROVED: Error handling for remote set imports and CDN index fetching",
                    "IMPROVED: Modular architecture for easy API integration (see API_REQUIREMENTS.md)",
                    "IMPROVED: Remote URL tab styling (blue-colored tab for visual distinction)"
                ]
            },
            {
                title: "Known Issues",
                items: [
                    "KNOWN: CORS configuration issue on CDN - duplicate Access-Control-Allow-Origin headers (server-side fix required)",
                    "WORKAROUND: Manual remote URL entry still works, CDN search requires CORS fix"
                ]
            }
        ]
    },
    {
        version: "3.7.0",
        title: "Grid Page & WEBP Export",
        date: new Date().toISOString().slice(0,10),
        isOpen: true,
        changes: [
            {
                title: "General UI & Logic",
                items: [
                    "ADDED: New /grid page with themed layout and left app drawer.",
                    "ADDED: Drag PNGs into a simple grid and export to WEBP.",
                    "MODULAR: Implemented export utility in src/utils/exportGridToWebp.js.",
                    "DEBUG: Recorded testing notes in changelog for visibility."
                ]
            }
        ]
    },
    {
        version: "3.6.0",
        title: "Professional Deck Export & Image Suite",
        date: "2024-12-19",
        isOpen: true,
        changes: [
            {
                title: "🚀 Major New Features",
                items: [
                    "ADDED: Enhanced Deck Image Editor with interactive text watermarks and full typography controls",
                    "ADDED: Professional layout positioning system with alignment toggles and manual offset controls", 
                    "ADDED: Canvas zoom functionality with zoom in/out, fit-to-screen, and percentage display",
                    "ADDED: Multi-resolution export presets including Discord Banner (960×540), 8K Ultra HD, and social media formats",
                    "NEW: PDF deck printing with customizable layouts.",
                    "NEW: Multiple card arrangements (grid layouts, stacked views, showcase modes) for printing",
                    "ENHANCED: Multi-format deck export controller with expanded format support",
                    "ADDED: Banner Creator with multiple background types and advanced image filters"
                ]
            },
            {
                title: "🔧 Technical Improvements",
                items: [
                    "OPTIMIZED: Image caching system for faster card loading",
                    "OPTIMIZED: Canvas rendering with requestAnimationFrame for smooth animations",
                    "ADDED: CORS proxy server for enhanced image URL compatibility",
                    "ENHANCED: Error handling and user feedback systems across all new features"
                ]
            },
            {
                title: "📝 Content Creator Features",
                items: [
                    "Discord Integration: Native Discord banner size support for server announcements",
                    "Social Media Ready: Pre-configured export sizes for Instagram, Twitter, and other platforms",
                    "Professional Quality: 8K Ultra HD export support for tournament displays",
                    "Watermarking: Customizable text watermarks for content attribution and branding",
                    "Layout Flexibility: Complete control over card positioning for unique deck showcases"
                ]
            }
        ]
    },
    {
        version: "3.5.0",
    date: "2025-01-02",
    title: "Forte Sets Integration & Major Enhancements",
    isOpen: true,
    changes: [
      {
        title: "🌟 Major New Features",
        items: [
          "**Forte Sets System:**",
          "ADDED: Permanent Forte Sets tab with dedicated sub-tabs for 'Forte Arrivals' (PF1), 'Celestial Resonance' (PF1a), and 'Ancient Awakenings' (PF1b).",
          "ADDED: Complete Forte PF1 dataset with 124 cards, including full metadata and GitHub-hosted images.",
          "ADDED: Forte tab positioned strategically between Custom Cards and imported sets for optimal workflow.",
          "ADDED: Default sort by card number for Forte sets, ensuring proper sequential browsing.",
          "ADDED: Full type filtering (All, Pokémon, Trainer, Energy, Basic Energy, Special Energy) for Forte cards.",
          "ADDED: Comprehensive sorting options (Name, Set Number, Import Order, Type, HP, Rarity) with compact UI controls.",
          "**Enhanced Drag & Drop System:**",
          "IMPROVED: Universal drag functionality now works across Search, Custom, Forte, and Favorites tabs.",
          "FIXED: Eliminated drag flickering by implementing anti-flickering CSS system with dynamic .dragging class.",
          "ADDED: Smooth drag operations with disabled transitions and hover effects during drag.",
          "IMPROVED: Drag state management with proper cleanup and visual feedback.",
          "**Custom Set Import Improvements:**",
          "FIXED: Eliminated duplicate tab creation during custom set imports through consolidated state management.",
          "IMPROVED: Single source of truth for set tab creation via loadCustomCards() function.",
          "ADDED: Proper tab switching after import with target set detection.",
          "IMPROVED: Race condition elimination in handleCustomCardImportComplete function."
        ]
      },
      {
        title: "🔧 Technical Enhancements",
        items: [
          "**Card Container System:**",
          "ADDED: Support for 'Forte' container type in CardContainer.js with proper recognition and tooltips.",
          "IMPROVED: Enhanced empty state handling for all container types.",
          "ADDED: Consistent card interaction patterns across all tabs.",
          "**Custom Card Management:**",
          "ADDED: createCustomCard() method to CustomCardManager for API compatibility.",
          "IMPROVED: Async wrapper around existing addCard() functionality.",
          "FIXED: Method resolution errors in CustomCardCreator component.",
          "**Image Loading & Caching:**",
          "IMPROVED: Enhanced ImageCacheService for handling external GitHub URLs.",
          "ADDED: Multiple fetch strategies for image loading.",
          "IMPROVED: Error handling and fallback mechanisms for image display.",
          "**File Access & Data Loading:**",
          "MOVED: Forte JSON data from src/ to public/ folder for proper HTTP accessibility.",
          "IMPROVED: Batch processing for card loading with progress tracking.",
          "ADDED: Comprehensive error handling for file access issues."
        ]
      },
      {
        title: "🎨 User Interface Improvements",
        items: [
          "**Forte Tab Design:**",
          "ADDED: Modern pills variant for Forte sub-tabs with clean appearance.",
          "IMPROVED: Compact spacing and reduced margins for better screen utilization.",
          "ADDED: 'Coming Soon' indicators for unavailable sets (PF1a, PF1b).",
          "ADDED: Magnifying glass icon for Forte Sets tab consistency.",
          "**Filter & Sort Controls:**",
          "IMPROVED: Compact type filter buttons with smaller font and padding.",
          "IMPROVED: Constrained sort dropdown width (140px) for space efficiency.",
          "ADDED: Enhanced visual feedback for active filters and sort options.",
          "**Debug & Development Tools:**",
          "ADDED: Consolidated debug tools.",
          "ADDED: Manual reload, state logging, card data inspection, and image URL testing.",
          "IMPROVED: Single debug button replacing multiple scattered debug controls."
        ]
      },
      {
        title: "🐛 Critical Bug Fixes",
        items: [
          "**Infinite Loading Resolution:**",
          "FIXED: Eliminated infinite loading loops in loadForteCards function.",
          "REMOVED: Problematic dependency arrays causing function recreation.",
          "ADDED: forteLoadingRef guard to prevent concurrent loading operations.",
          "**Drag Functionality Restoration:**",
          "FIXED: Drag operations now work in all tabs (Custom, Forte, Favorites) not just Search.",
          "UPDATED: PkmnCard.js to allow dragging from multiple container types.",
          "IMPROVED: Consistent drag behavior across the entire application.",
          "**Import Duplicate Prevention:**",
          "FIXED: Custom set imports no longer create duplicate tabs.",
          "IMPROVED: Consolidated set creation logic to prevent race conditions.",
          "SIMPLIFIED: Import completion handler to focus only on tab switching.",
          "**Compilation & Merge Conflicts:**",
          "RESOLVED: All merge conflict markers and compilation errors.",
          "CLEANED: Removed unused imports and modal references.",
          "FIXED: Parsing errors in CustomCardCreator.js."
        ]
      },
      {
        title: "⚡ Performance Optimizations",
        items: [
          "**Loading Efficiency:**",
          "IMPROVED: Batch processing for Forte card loading with configurable batch sizes.",
          "ADDED: Progress tracking and user feedback during data loading.",
          "OPTIMIZED: Memory usage through efficient card data structure.",
          "**State Management:**",
          "IMPROVED: Reduced unnecessary re-renders through optimized useEffect dependencies.",
          "ADDED: Proper cleanup and garbage collection for card loading operations.",
          "OPTIMIZED: Tab switching performance with targeted state updates.",
          "**CSS & Styling:**",
          "ADDED: Anti-flickering system for smooth drag operations.",
          "IMPROVED: Transition management during user interactions.",
          "OPTIMIZED: CSS class application for better performance."
        ]
      },
      {
        title: "🔄 Compatibility & Standards",
        items: [
          "**Cross-Platform Support:**",
          "IMPROVED: Enhanced compatibility with Windows PowerShell environments.",
          "ADDED: Proper port management for development server conflicts.",
          "IMPROVED: File path handling for different operating systems.",
          "**Data Format Standards:**",
          "MAINTAINED: Consistent card data structure across all sources.",
          "IMPROVED: Robust JSON parsing and validation.",
          "ADDED: Fallback mechanisms for data loading failures.",
          "**API Integration:**",
          "ENHANCED: Better error handling for external image URLs.",
          "IMPROVED: GitHub integration for Forte card images.",
          "ADDED: Multiple fetch strategies for reliability."
        ]
      },
      {
        title: "📝 Developer Experience",
        items: [
          "**Debugging Tools:**",
          "ADDED: Comprehensive debug console with organized output sections.",
          "IMPROVED: State inspection tools for troubleshooting.",
          "ADDED: Image URL testing and validation utilities.",
          "**Code Organization:**",
          "IMPROVED: Cleaner separation of concerns in tab management.",
          "ADDED: Better documentation and inline comments.",
          "ENHANCED: Error logging and diagnostic information.",
          "**Development Workflow:**",
          "IMPROVED: Hot reload compatibility with proper state preservation.",
          "ADDED: Better development server port management.",
          "ENHANCED: Build process stability and error reporting."
        ]
      }
    ]
  },
  {
    version: "2.0.0",
    date: "2025-05-20", // Ensure this date is current for release
    title: "2.0 First Official Meta-PTCG Fork",
    isOpen: true, // This version's details will be open by default
    changes: [
      {
        title: "Key Features & Enhancements",
        items: [
          "**Revamped Card Search Panel (`EnhancedCardSearchPanel.js`):**",
          "ADDED: Default Load: Displays cards from the latest Pokémon TCG set on initial load for faster startup.",
          "CHANGED: Manual Search Trigger: Card searches are now initiated only by pressing the \"Search\" button.",
          "IMPROVED: Filter Panel Behavior: Search filter accordion now automatically minimizes when a search is executed.",
          "IMPROVED: Card Preview: Clicking a card image in search results now opens an enhanced preview modal (`CardPreviewModal.js`).",
          "ADDED: Custom Set Tabs: Integrated `CustomSetTabs.js` for managing and navigating user-created custom card sets.",
          "**Advanced Deck Export (`EnhancedExportModal.js`):**",
          "ADDED: Export modal with Text, CSV, XML, and JSON, plus optional extra card data.",
          "**Deck Image Generation (`ImprovedDeckImageModal.js`):**",
          "ADDED: New modal for creating customizable deck images with various layouts (grid, stacked, fanned, showcase), backgrounds, card counters, and optional chart overlays.",
          "ADDED: Features for sorting cards within the image, undo/redo functionality, and style presets.",
          "**Custom Card Management (`CustomCardManager.js`, `EnhancedTCGController.js`):**",
          "IMPROVED: Robust system for creating, updating, deleting, and organizing custom cards.",
          "ADDED: Support for assigning custom cards to user-defined sets via `CustomSetTabs.js` and `CustomCardImportExport.js`.",
          "**Deck Persistence & Import (`DeckViewPanel.js`, `StorageManager.js`, `ImportValidator.js`):**",
          "IMPROVED: Work-in-progress decks are now automatically saved to local storage and reloaded on app start.",
          "ADDED: Unified `StorageManager.js` for consistent data handling, including chunking for large datasets.",
          "ADDED: `ImportValidator.js` for validating decklist formats before import.",
          "**Drag and Drop (`DeckViewPanel.js`, `CardContainer.js`):**",
          "IMPROVED: Drag and drop functionality for adding cards to the deck from search results.",
        ],
      },
      {
        title: "Performance & Optimization",
        items: [
          "**Optimized Image Loading (`ImageOptimizer.js`, `PkmnCard.js`):**",
          "ADDED: Introduced `ImageOptimizer.js` to manage image URLs (small/large versions).",
          "IMPROVED: Card displays now prioritize smaller resolution images for faster grid loading.",
          "IMPROVED: `PkmnCard.js` features improved image loading with caching (`ImageCacheService`), error handling, retries, and fallback displays.",
          "**Optimized Initial Load (`EnhancedCardSearchPanel.js`):**",
          "IMPROVED: Loads only the latest set by default, significantly reducing initial data fetch and rendering time.",
          "**Efficient Data Storage (`StorageManager.js`, `EnhancedTCGController.js`):**",
          "ADDED: Implemented chunking for custom card storage in `EnhancedTCGController.js` and generalized in `StorageManager.js` to handle large amounts of data.",
          "IMPROVED: Optimized storage for favorites by compressing card data.",
        ],
      },
      {
        title: "Bug Fixes",
        items: [
          "**Card Preview (`EnhancedCardSearchPanel.js`, `CardPreviewModal.js`):**",
          "FIXED: Resolved issues with the card preview button; clicking card images now reliably opens the preview modal.",
          "**Search Functionality (`EnhancedCardSearchPanel.js`):**",
          "FIXED: Corrected syntax errors in API query construction within `performSearch`, ensuring proper search execution and filter application.",
          "FIXED: Addressed issues with saving and loading work-in-progress decks for improved data integrity.",
          "**General UI & Logic:**",
          "FIXED: Numerous small fixes across components to support the above features and enhance stability.",
        ],
      },
      {
        title: "Code & System Refinements",
        items: [
          "**Modular Design:**",
          "ADDED: New utility modules (`ImageOptimizer.js`, `ImportValidator.js`, `StorageManager.js`).",
          "**Controller Logic (`EnhancedTCGController.js`, `TCGController.js`):**",
          "IMPROVED: Refined API interaction logic, including methods for fetching sets, rarities, and subtypes.",
          "IMPROVED: `query` method in `EnhancedTCGController.js` to handle API and custom card results, and correctly format API parameters.",
          "**Component Updates (`PkmnCard.js`, `CardContainer.js`, `CustomSetTabs.js`, etc.):**",
          "IMPROVED: Updated various components for better state management, prop handling, and UI consistency.",
          "**CSS Overhaul (`CardSearchPanel.module.css`):**",
          "IMPROVED: Comprehensive update to styles for improved layout, loading indicators, dark theme support, and overall visual polish.",
        ],
      },
    ],
  },
  {
    version: "1.2.0",
    date: "2025-05-17",
    title: "Custom Card & Export Enhancements",
    isOpen: false, 
    changes: [
      {
        title: "Major Changes",
        items: [
          "**Custom Card Creation System:**",
          "CHANGED: Replaced image upload with direct URL input for custom cards, simplifying the process and improving reliability.",
          "FIXED: Resolved storage quota errors previously encountered when creating multiple custom cards by storing only image URLs.",
          "ADDED: Image preview for direct URLs during custom card creation, with error validation to ensure link viability.",
          "IMPROVED: Enhanced compatibility with deck importers like PTCG Live and TCG Simulator by using standardized direct image URLs.",
          "**Deck Import/Export System:**",
          "FIXED: CSV exports now consistently use direct image URLs instead of `data:image` strings, ensuring better compatibility and smaller file sizes.",
          "IMPROVED: Exported CSV files are now significantly smaller due to the removal of embedded image data.",
          "ADDED: Default placeholder images are now used for different card types (Pokémon, Trainer, Energy) if a custom card's URL is invalid or missing during export.",
          "FIXED: Importing and exporting Trainer and Energy cards now works more reliably with accurate type and image handling.",
          "**Data Storage Optimizations:**",
          "REDUCED: Storage requirements for custom cards have been reduced by over 90% by storing image URLs instead of base64 encoded images.",
          "ADDED: Automatic cleanup of the oldest custom cards when the maximum storage limit (currently 50 cards) is reached, preventing browser storage quota errors.",
          "FIXED: Eliminated browser storage quota errors that could occur when saving a large number of custom cards.",
        ],
      },
      {
        title: "Technical Improvements",
        items: [
          "**Enhanced TCG Controller (`src/utils/TCGapi/EnhancedTCGController.js`):**",
          "CHANGED: Version upgraded to 1.4.0.",
          "REMOVED: Complex client-side image processing and compression for custom cards.",
          "ADDED: Direct URL storage mechanism for custom card images.",
          "IMPROVED: Error handling for storage limits and automatic cleanup of old custom card data.",
          "**Custom Card Creator (`src/components/modals/CustomCardCreator.js`):**",
          "REPLACED: File upload input with a direct URL input field for card images.",
          "ADDED: URL validation with visual feedback (e.g., success/error indicators) for the image link.",
          "ADDED: Live image preview directly from the provided URL within the creator modal.",
          "IMPROVED: Form validation and error handling for a smoother user experience.",
          "ADDED: Help text guiding users on compatible image hosts and URL types.",
          "**TCG Sim Controller (`src/utils/TCGsim/TCGSimController.js`):**",
          "ADDED: Export-friendly URL formatting. Google Drive share links are rewritten when possible; direct image links are still preferred.",
          "ADDED: Robust placeholder images for different card types (Pokémon, Trainer, Energy) during export if an image URL is problematic.",
          "IMPROVED: CSV formatting (quoting fields) for better compatibility with a wider range of spreadsheet programs and simulators.",
          "IMPROVED: Error handling for malformed input during CSV import and export processes.",
        ],
      },
      {
        title: "Compatibility Notes",
        items: [
          "Direct image URLs (e.g., ending in `.png`, `.jpg`) must be used for custom cards for full compatibility, especially with external tools. These can be hosted on services like Imgur, ImgBB, or your own web server.",
          "While the application attempts to process Google Drive viewing page links for in-app display and export, they are not guaranteed to work reliably with all external simulators. Direct image links are strongly recommended.",
          "Existing custom cards created with `data:` URLs (from previous versions) will still display correctly within the application but will be exported using placeholder images to ensure compatibility and small file sizes.",
        ],
      },
      {
        title: "Known Issues",
        items: [
          "Google Drive or similar viewing page links (e.g., `drive.google.com/file/d/.../view`) might not render correctly in all external tools or simulators even after transformation. Always prefer direct image URLs.",
          "Only permanent, publicly accessible direct image URLs ending with common image file extensions (e.g., `.jpg`, `.png`, `.gif`) are guaranteed to be fully compatible for custom cards, especially for third-party tools.",
          "Image URLs must be publicly accessible and not require any login or special permissions to view.",
        ],
      },
      {
        title: "Summary",
        items: [
          "This update significantly improves the reliability and usability of custom cards, enhances compatibility with external deck importers/simulators, and resolves critical storage limitations, leading to a much smoother user experience.",
        ]
      }
    ],
  },
  {
    version: "1.1.0",
    date: "2025-04-15",
    title: "Proxy Support & Early Fixes",
    isOpen: false,
    changes: [
      {
        title: "What's New",
        items: ["ADDED: The ability to toggle between JP and English card proxies now available. Note, some cards will not have english proxies. Proxies are taken from JustInBasil."],
      },
      {
        title: "Bug Fixes",
        items: ["FIXED: Issue where cards imported with type \"Pokemon\" (instead of \"Pokémon\") failed to import."],
      },
      {
        title: "Supported Prereleased Sets",
        items: ["Twilight Masquerade", "Temporal Forces (logo only shown as example)"],
      }
    ]
  }
];

export const latestVersion = changelogData[0]; 

export default changelogData;
